/* eslint-disable max-lines */
import {
  ArrowsRightLeftIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  StarIcon
} from "@heroicons/react/24/outline";
import { FC } from "react";
import { IReservationV2 } from "../../interfaces/reservations/Reservations.types";
import { IReservationPaymentBreakdown } from "../../interfaces/wallet/Wallet.types";
import { TicketStatusEnum } from "../reservations/res.enum";
import InsertDate from "./InsertDate";
import PaymentChip from "./PaymentChip";
import LuggageBadge from "./luggage/LuggageBadge";
import PassengersBadge from "./passengers/PassengersBadge";

interface PaymentInfoProps {
  paymentStatus: IReservationV2["paymentStatus"];
  paymentMethodId: IReservationV2["paymentMethodId"];
  price: IReservationV2["price"];
  clientNames: IReservationV2["clientNames"];
  insertDate?: IReservationV2["insertDate"]; // Make it optional using "?"
  conciergeName?: IReservationV2["conciergeName"];
  flexType?: IReservationV2["flexType"];
  hasTextMeMyTrip?: IReservationV2["hasTextMeMyTrip"];
  hasOpenReturn?: IReservationV2["hasOpenReturn"];
  hasLuggage?: IReservationV2["hasLuggage"];
  uniqueJourneyId?: IReservationV2["uniqueJourneyId"];
  onLuggageChange?: () => void;
  /** Refetch after a SuperAdmin passenger name correction. Separate from onLuggageChange so
   * the two badges stay independent even though both currently refetch the same data. */
  onPassengersChange?: () => void;
  cancellationSource?: IReservationV2["cancellationSource"];
  /** The booking is still active, but one of its tickets was cancelled on its own. */
  partiallyCanceled?: boolean;
  /** How the booking was paid; a split payment shows its two parts under the chip. Absent for admins
   * without wallet.credit.read. */
  breakdown?: IReservationPaymentBreakdown;
}

const PaymentInfo: FC<PaymentInfoProps> = ({
  paymentStatus,
  paymentMethodId,
  price,
  clientNames,
  insertDate,
  conciergeName,
  flexType,
  hasTextMeMyTrip,
  hasOpenReturn,
  hasLuggage,
  uniqueJourneyId,
  onLuggageChange,
  onPassengersChange,
  cancellationSource,
  breakdown,
  partiallyCanceled
}) => {

  const showAdminCancellationBadge =
    paymentStatus === TicketStatusEnum.Canceled &&
    cancellationSource === "admin";

  return (
    <div className="col-start-7 col-span-4 row-start-1 flex flex-col space-x-4">
      <div className="flex justify-end">
        {clientNames?.length ? (
          <div className="flex items-center mr-5">
            <PassengersBadge
              count={clientNames.length}
              uniqueJourneyId={uniqueJourneyId}
              onSuccess={onPassengersChange}
            />
          </div>
        ) : null}
        <PaymentChip paymentStatus={paymentStatus} paymentMethodId={paymentMethodId} price={price}
          breakdown={breakdown} />
      </div>
      <div className="flex justify-end items-center gap-2">
        {partiallyCanceled && (
          <div
            className="inline-flex items-center px-2 py-0.5 rounded border border-orange-300
          bg-orange-100/30 text-orange-900 text-xs font-semibold"
            title="Një biletë e këtij rezervimi është anuluar. Biletat e tjera mbeten të vlefshme."
          >
            <span>Pjesërisht i anuluar</span>
          </div>
        )}
        {showAdminCancellationBadge && (
          <div className="inline-flex items-center px-2 py-0.5 rounded
          border border-red-300 bg-red-100/50 text-red-700 text-xs font-semibold">
            <span>Admin</span>
          </div>
        )}
        {(hasOpenReturn ||
          conciergeName ||
          (flexType && flexType !== "NotRefundable")) && (
          <div className="flex gap-1 mt-1 justify-end">
            {hasOpenReturn && (
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded
              text-[11px] text-white bg-warning-300">
                <ArrowsRightLeftIcon className="w-3 h-3 text-white" />
                <span>Open return</span>
              </div>
            )}
            {conciergeName && (
              <div className="inline-flex items-center gap-1 bg-blue-100
             text-amber-700 px-2 py-0.5 rounded text-xs">
                <StarIcon className="w-3 h-3 text-secondary fill-primary" />
                <span>Concierge</span>
              </div>
            )}
            {flexType && flexType !== "NotRefundable" && (
              <div className="inline-flex items-center gap-1 bg-orange-100/50
            text-blue-700 px-2 py-0.5 rounded text-xs">
                <ArrowsRightLeftIcon className="w-3 h-3 text-secondary" />
                <span>{flexType}</span>
              </div>
            )}
          </div>
        )}
        {hasTextMeMyTrip && (
          <div className="flex gap-1 mt-1 justify-end">
            <div className="inline-flex items-center gap-1 bg-blue-300
             text-white px-2 py-0.5 rounded text-xs">
              <ChatBubbleOvalLeftEllipsisIcon className="w-3.5 h-3.5 text-white " />
              <span>SMS</span>
            </div>
          </div>
        )}
        {hasLuggage && (
          <LuggageBadge
            uniqueJourneyId={uniqueJourneyId}
            onSuccess={onLuggageChange}
          />
        )}
      </div>
      {insertDate && <InsertDate insertDate={insertDate} />}
    </div>
  );
};

export default PaymentInfo;
